import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp, TrendingDown, Calendar, Users, Package, Snowflake, AlertTriangle,
  Wallet, Target, Trophy, Layers, UserCheck, HelpCircle, ShoppingCart, Boxes,
  Loader2, BarChart3, Percent,
} from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, AreaChart, Area, Legend,
} from 'recharts'
import { api, formatMoney } from '@/lib/api'

const PALETA = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16', '#a855f7', '#0ea5e9']
const ABC_CORES: any = { A: '#10b981', B: '#f59e0b', C: '#ef4444' }
const COBRANCA_LABEL: any = {
  dinheiro: 'Dinheiro', boleto: 'Boleto', prazo: 'A prazo', cheque: 'Cheque',
  pix: 'PIX', cartao_credito: 'Cartão', '—': 'Não informado',
}

const PERIODOS = [
  ['mes', 'Mês atual'], ['mes_anterior', 'Mês anterior'], ['hoje', 'Hoje'],
  ['ult30', 'Últimos 30 dias'], ['ult90', 'Últimos 90 dias'], ['personalizado', 'Personalizado'],
] as const

function diaCurto(iso: string) {
  const [, m, d] = iso.split('-'); return `${d}/${m}`
}

// Texto de ajuda (tooltip) ao lado dos títulos
function Ajuda({ children }: { children: React.ReactNode }) {
  return (
    <span className="group relative inline-flex">
      <HelpCircle size={14} className="text-ink-400 cursor-help" />
      <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 top-5 z-20 w-60
        bg-ink-800 text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-lg font-normal">
        {children}
      </span>
    </span>
  )
}

function Secao({ titulo, icon, ajuda, children, className = '' }: any) {
  return (
    <div className={`bg-white rounded-2xl border border-ink-200 shadow-sm p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-semibold text-ink-800 text-sm">{titulo}</h3>
        {ajuda && <Ajuda>{ajuda}</Ajuda>}
      </div>
      {children}
    </div>
  )
}

// ============================================================================
export default function VendaGeralPage() {
  const [aba, setAba] = useState<'geral' | 'vendedor'>('geral')
  const [periodo, setPeriodo] = useState('mes')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')

  const params: any = periodo === 'personalizado' && de && ate ? { de, ate } : { periodo }

  return (
    <div className="p-4 lg:p-6 space-y-4 bg-ink-50/40 min-h-full">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-ink-800">
            <BarChart3 className="text-violet-600" size={26} /> Venda Geral
          </h1>
          <p className="text-xs text-ink-500 mt-0.5">Painel comercial — vendas, vendedores, clientes, produtos e estoque num só lugar.</p>
        </div>
        {/* Seletor de período */}
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={16} className="text-ink-400" />
          <select value={periodo} onChange={e => setPeriodo(e.target.value)}
            className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white">
            {PERIODOS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {periodo === 'personalizado' && (
            <>
              <input type="date" value={de} onChange={e => setDe(e.target.value)}
                className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white" />
              <span className="text-ink-400 text-sm">até</span>
              <input type="date" value={ate} onChange={e => setAte(e.target.value)}
                className="text-sm border border-ink-300 rounded-lg px-2 py-1.5 bg-white" />
            </>
          )}
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-1 border-b border-ink-200">
        {([['geral', 'Visão Geral'], ['vendedor', 'Por Vendedor']] as const).map(([k, lbl]) => (
          <button key={k} onClick={() => setAba(k)}
            className={`px-5 py-2 text-sm font-medium -mb-px border-b-2 transition ${
              aba === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-ink-500 hover:text-ink-700'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {aba === 'geral' ? <VisaoGeral params={params} /> : <PorVendedor params={params} />}
    </div>
  )
}

// ============================================================================
// ABA 1 — VISÃO GERAL
// ============================================================================
function VisaoGeral({ params }: { params: any }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['venda-geral', params],
    queryFn: async () => (await api.get('/comercial/venda-geral', { params })).data,
  })

  if (isLoading) return <CarregandoBox />
  if (!data) return null
  const c = data.cards

  return (
    <div className="space-y-4">
      {/* CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CardKPI titulo="Hoje" valor={c.hoje.total} cor="blue" icon={<ShoppingCart size={18} />}
          sub={`${c.hoje.qtd} pedidos · ticket ${formatMoney(c.hoje.ticket)}`}
          ajuda="Total vendido hoje (fuso de Fortaleza). Conta pedidos confirmados, exclui bonificação e troca." />
        <CardKPI titulo="Mês atual" valor={c.mes_atual.total} cor="violet" icon={<TrendingUp size={18} />}
          sub={`${c.mes_atual.qtd} pedidos · ticket ${formatMoney(c.mes_atual.ticket)}`}
          badge={c.variacao_pct != null ? c.variacao_pct : undefined}
          extra={<span className="text-[11px] text-violet-600">Projeção do mês: <b>{formatMoney(c.mes_atual.projecao)}</b></span>}
          ajuda="Vendas do mês corrente. A projeção estima o fechamento com base no ritmo diário até hoje. A seta compara com o mês anterior." />
        <CardKPI titulo="Mês anterior" valor={c.mes_anterior.total} cor="slate" icon={<Calendar size={18} />}
          sub={`${c.mes_anterior.qtd} pedidos · ticket ${formatMoney(c.mes_anterior.ticket)}`}
          ajuda="Total fechado no mês passado — base de comparação." />
      </div>

      {/* SÉRIE DIÁRIA */}
      <Secao titulo={`Evolução diária — ${data.periodo.label}`} icon={<TrendingUp size={16} className="text-violet-600" />}
        ajuda="Quanto foi vendido por dia no período selecionado. Útil pra ver picos, quedas e dias parados.">
        {data.serie.length === 0 ? <Vazio /> : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.serie}>
              <defs>
                <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="data" tickFormatter={diaCurto} fontSize={11} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
              <Tooltip formatter={(v: any) => formatMoney(v)} labelFormatter={diaCurto} />
              <Area type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} fill="url(#gv)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Secao>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* POR VENDEDOR */}
        <Secao titulo="Vendas por vendedor" icon={<Users size={16} className="text-blue-600" />}
          ajuda="Total vendido por cada vendedor no período. A projeção estima onde ele chega no fim do período mantendo o ritmo.">
          {data.por_vendedor.length === 0 ? <Vazio /> : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-ink-500 text-xs border-b">
                  <th className="text-left py-1.5">Vendedor</th><th className="text-right">Pedidos</th>
                  <th className="text-right">Venda</th><th className="text-right">Projeção</th>
                </tr></thead>
                <tbody>
                  {data.por_vendedor.map((v: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                      <td className="py-1.5 font-medium">{v.vendedor}</td>
                      <td className="text-right text-ink-500">{v.qtd}</td>
                      <td className="text-right font-semibold">{formatMoney(v.total)}</td>
                      <td className="text-right text-violet-600">{formatMoney(v.projecao)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Secao>

        {/* COBRANÇA */}
        <Secao titulo="Venda por cobrança" icon={<Wallet size={16} className="text-emerald-600" />}
          ajuda="Como as vendas se dividem por forma de pagamento (dinheiro, boleto, a prazo, etc.).">
          {data.por_cobranca.length === 0 ? <Vazio /> : (
            <div className="flex items-center gap-3">
              <ResponsiveContainer width="55%" height={180}>
                <PieChart>
                  <Pie data={data.por_cobranca} dataKey="total" nameKey="forma" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {data.por_cobranca.map((_: any, i: number) => <Cell key={i} fill={PALETA[i % PALETA.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => formatMoney(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1 text-sm">
                {data.por_cobranca.map((f: any, i: number) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: PALETA[i % PALETA.length] }} />
                    <span className="flex-1">{COBRANCA_LABEL[f.forma] || f.forma}</span>
                    <span className="font-semibold">{formatMoney(f.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Secao>

        {/* TOP CLIENTES */}
        <Secao titulo="Top 10 clientes" icon={<Trophy size={16} className="text-amber-500" />}
          ajuda="Os 10 clientes que mais compraram no período (por valor).">
          <RankingBarras itens={data.top_clientes} cor="#3b82f6" />
        </Secao>

        {/* TOP PRODUTOS */}
        <Secao titulo="Top 10 produtos mais vendidos" icon={<Package size={16} className="text-violet-600" />}
          ajuda="Os 10 produtos com maior valor de venda no período. A quantidade aparece no tooltip.">
          <RankingBarras itens={data.top_produtos} cor="#8b5cf6" mostrarQtd />
        </Secao>

        {/* ABC CLIENTES */}
        <Secao titulo="Curva ABC — Clientes" icon={<Layers size={16} className="text-blue-600" />}
          ajuda="Princípio de Pareto: A = poucos clientes que geram até 80% do faturamento; B = próximos 15%; C = a cauda longa. Foque em manter os A.">
          <CurvaABC resumo={data.abc_clientes} unidade="clientes" />
        </Secao>

        {/* ABC PRODUTOS */}
        <Secao titulo="Curva ABC — Produtos" icon={<Layers size={16} className="text-violet-600" />}
          ajuda="A = produtos campeões (até 80% da venda); B = intermediários; C = giro baixo. Garanta estoque dos A e reveja os C.">
          <CurvaABC resumo={data.abc_produtos} unidade="produtos" />
        </Secao>

        {/* SEM GIRO */}
        <Secao titulo="Sem giro — capital parado" icon={<Boxes size={16} className="text-rose-600" />}
          ajuda={`Produtos com estoque mas SEM venda há mais de 60 dias. É dinheiro parado na prateleira. (${data.sem_giro_total} no total)`}>
          {data.sem_giro.length === 0 ? <Vazio txt="Nenhum produto parado 🎉" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-ink-500 text-xs border-b">
                <th className="text-left py-1.5">Produto</th><th className="text-right">Estoque</th>
                <th className="text-right">Parado (R$)</th><th className="text-right">S/ venda</th>
              </tr></thead>
              <tbody>
                {data.sem_giro.map((p: any, i: number) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                    <td className="py-1.5 truncate max-w-[180px]" title={p.descricao}>{p.codigo} · {p.descricao}</td>
                    <td className="text-right text-ink-500">{p.estoque}</td>
                    <td className="text-right font-semibold text-rose-600">{formatMoney(p.valor_parado)}</td>
                    <td className="text-right text-ink-500">{p.dias_sem_venda != null ? `${p.dias_sem_venda}d` : 'nunca'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Secao>

        {/* ESTOQUE BAIXO */}
        <Secao titulo="Estoque baixo — risco de ruptura" icon={<AlertTriangle size={16} className="text-amber-600" />}
          ajuda={`Produtos no/abaixo do estoque mínimo — candidatos a comprar pra não faltar. (${data.estoque_baixo_total} no total)`}>
          {data.estoque_baixo.length === 0 ? <Vazio txt="Tudo acima do mínimo 👍" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-ink-500 text-xs border-b">
                <th className="text-left py-1.5">Produto</th><th className="text-right">Atual</th>
                <th className="text-right">Mínimo</th><th className="text-right">Falta</th>
              </tr></thead>
              <tbody>
                {data.estoque_baixo.map((p: any, i: number) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-ink-50">
                    <td className="py-1.5 truncate max-w-[180px]" title={p.descricao}>{p.codigo} · {p.descricao}</td>
                    <td className="text-right text-amber-600 font-semibold">{p.estoque}</td>
                    <td className="text-right text-ink-500">{p.minimo}</td>
                    <td className="text-right text-rose-600">{p.falta}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Secao>
      </div>
    </div>
  )
}

// ============================================================================
// ABA 2 — POR VENDEDOR
// ============================================================================
function PorVendedor({ params }: { params: any }) {
  const [vendedorId, setVendedorId] = useState<string>('')
  const [meta, setMeta] = useState<string>('')

  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['comercial-vendedores'],
    queryFn: async () => (await api.get('/comercial/vendedores')).data,
  })

  const q: any = { ...params, vendedor_id: vendedorId }
  if (meta) q.meta = meta
  const { data, isLoading } = useQuery<any>({
    queryKey: ['venda-geral-vendedor', q],
    queryFn: async () => (await api.get('/comercial/venda-geral/vendedor', { params: q })).data,
    enabled: !!vendedorId,
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap bg-white rounded-2xl border border-ink-200 p-4 shadow-sm">
        <UserCheck size={18} className="text-violet-600" />
        <select value={vendedorId} onChange={e => setVendedorId(e.target.value)}
          className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white min-w-[220px]">
          <option value="">Escolha o vendedor…</option>
          {vendedores.map((v: any) => <option key={v.id} value={v.id}>{v.nome}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <Target size={15} className="text-ink-400" />
          <input type="number" value={meta} onChange={e => setMeta(e.target.value)} placeholder="Meta do mês (R$)"
            className="text-sm border border-ink-300 rounded-lg px-3 py-1.5 bg-white w-40" />
          <Ajuda>Digite a meta de vendas do vendedor pra ver quanto já atingiu e se a projeção bate a meta.</Ajuda>
        </div>
      </div>

      {!vendedorId && (
        <div className="text-center py-16 text-ink-400">
          <UserCheck size={40} className="mx-auto mb-2 opacity-40" />
          <p className="font-medium">Selecione um vendedor para ver a vida dele</p>
        </div>
      )}

      {vendedorId && isLoading && <CarregandoBox />}

      {vendedorId && data && (
        <>
          {/* CARDS DO VENDEDOR */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <CardKPI titulo="Venda no período" valor={data.resumo.total} cor="violet" icon={<Wallet size={18} />}
              sub={`${data.resumo.qtd_pedidos} pedidos · ticket ${formatMoney(data.resumo.ticket)}`}
              badge={data.mes_anterior.variacao_pct ?? undefined}
              ajuda="Total vendido pelo vendedor no período. A seta compara com o mês anterior dele." />
            <CardKPI titulo="Projeção" valor={data.resumo.projecao} cor="blue" icon={<TrendingUp size={18} />}
              sub={`ritmo de ${data.periodo.dias_corridos}/${data.periodo.dias_periodo} dias`}
              ajuda="Onde o vendedor chega no fim do período se mantiver o ritmo atual." />
            <CardKPI titulo="Positivação" valor={data.resumo.positivacao_pct != null ? `${data.resumo.positivacao_pct}%` : '—'} texto cor="emerald" icon={<UserCheck size={18} />}
              sub={`${data.resumo.clientes_atendidos} de ${data.resumo.carteira} clientes`}
              ajuda="% da carteira que comprou no período. Positivação alta = boa cobertura dos clientes dele." />
            <CardKPI titulo="Mix de produtos" valor={String(data.resumo.mix_produtos)} texto cor="amber" icon={<Layers size={18} />}
              sub="produtos distintos vendidos"
              ajuda="Quantos produtos diferentes o vendedor vendeu. Mix maior = venda menos concentrada num só item." />
          </div>

          {/* META */}
          {data.meta && (
            <Secao titulo="Meta do período" icon={<Target size={16} className="text-rose-600" />}
              ajuda="Comparação entre o realizado, a meta digitada e a projeção de fechamento.">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <MiniBox label="Meta" valor={formatMoney(data.meta.meta)} />
                <MiniBox label="Realizado" valor={`${data.meta.atingido_pct}%`} cor={data.meta.atingido_pct >= 100 ? 'emerald' : 'ink'} />
                <MiniBox label="Projetado" valor={`${data.meta.projetado_pct}%`} cor={data.meta.projetado_pct >= 100 ? 'emerald' : 'amber'} />
                <MiniBox label="Falta" valor={formatMoney(data.meta.falta)} cor={data.meta.falta > 0 ? 'rose' : 'emerald'} />
              </div>
              <div className="mt-3 h-3 rounded-full bg-ink-100 overflow-hidden">
                <div className="h-full bg-violet-500" style={{ width: `${Math.min(100, data.meta.atingido_pct)}%` }} />
              </div>
            </Secao>
          )}

          {/* AMARRADOS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Secao titulo="Clientes amarrados" icon={<Users size={16} className="text-blue-600" />}
              ajuda="Quantos clientes têm este vendedor na carteira (cadastro).">
              <div className="text-3xl font-bold text-blue-600">{data.amarrados.clientes}</div>
              <p className="text-xs text-ink-500 mt-1">{data.resumo.clientes_atendidos} compraram no período</p>
            </Secao>
            <Secao titulo="Freezers (comodato)" icon={<Snowflake size={16} className="text-cyan-600" />}
              ajuda="Freezers de comodato em uso nos clientes da carteira dele, e o valor patrimonial.">
              <div className="text-3xl font-bold text-cyan-600">{data.amarrados.freezers}</div>
              <p className="text-xs text-ink-500 mt-1">Valor: {formatMoney(data.amarrados.freezers_valor)}</p>
            </Secao>
            <Secao titulo="Comparativo" icon={<TrendingUp size={16} className="text-violet-600" />}
              ajuda="Venda do período vs mês anterior do vendedor.">
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-ink-500">Período</span><b>{formatMoney(data.resumo.total)}</b></div>
                <div className="flex justify-between"><span className="text-ink-500">Mês anterior</span><b>{formatMoney(data.mes_anterior.total)}</b></div>
                {data.mes_anterior.variacao_pct != null && (
                  <div className={`flex items-center gap-1 font-semibold ${data.mes_anterior.variacao_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {data.mes_anterior.variacao_pct >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {data.mes_anterior.variacao_pct >= 0 ? '+' : ''}{data.mes_anterior.variacao_pct}%
                  </div>
                )}
              </div>
            </Secao>
          </div>

          {/* SÉRIE */}
          <Secao titulo="Evolução diária do vendedor" icon={<TrendingUp size={16} className="text-violet-600" />}>
            {data.serie.length === 0 ? <Vazio /> : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data.serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="data" tickFormatter={diaCurto} fontSize={11} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
                  <Tooltip formatter={(v: any) => formatMoney(v)} labelFormatter={diaCurto} />
                  <Area type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} fill="#ede9fe" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Secao>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Secao titulo="Top clientes dele" icon={<Trophy size={16} className="text-amber-500" />}>
              <RankingBarras itens={data.top_clientes} cor="#3b82f6" />
            </Secao>
            <Secao titulo="Top produtos dele" icon={<Package size={16} className="text-violet-600" />}>
              <RankingBarras itens={data.top_produtos} cor="#8b5cf6" mostrarQtd />
            </Secao>
          </div>

          {/* INATIVOS */}
          <Secao titulo="Carteira inativa — oportunidade" icon={<AlertTriangle size={16} className="text-rose-600" />}
            ajuda="Clientes da carteira do vendedor que NÃO compraram no período. São oportunidades de reativação.">
            {data.inativos.length === 0 ? <Vazio txt="Todos os clientes compraram 🎉" /> : (
              <>
                <p className="text-xs text-ink-500 mb-2">{data.inativos_total} cliente(s) sem compra no período. Mostrando os mais antigos:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  {data.inativos.map((c: any, i: number) => (
                    <div key={i} className="flex justify-between border-b last:border-0 py-1">
                      <span className="truncate">{c.nome}</span>
                      <span className="text-ink-400 text-xs whitespace-nowrap ml-2">
                        {c.dias != null ? `há ${c.dias}d` : 'nunca comprou'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Secao>
        </>
      )}
    </div>
  )
}

// ============================================================================
// Componentes auxiliares
// ============================================================================
function CardKPI({ titulo, valor, sub, cor, icon, badge, extra, texto, ajuda }: any) {
  const cores: any = {
    violet: 'from-violet-500 to-violet-600', blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600', amber: 'from-amber-500 to-amber-600',
    slate: 'from-slate-400 to-slate-500',
  }
  return (
    <div className="bg-white rounded-2xl border border-ink-200 shadow-sm p-4 relative overflow-hidden">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${cores[cor]} text-white grid place-items-center`}>{icon}</div>
          <span className="text-sm font-medium text-ink-600">{titulo}</span>
          {ajuda && <Ajuda>{ajuda}</Ajuda>}
        </div>
        {badge != null && (
          <span className={`text-xs font-semibold flex items-center gap-0.5 px-1.5 py-0.5 rounded ${
            badge >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
            {badge >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{badge >= 0 ? '+' : ''}{badge}%
          </span>
        )}
      </div>
      <div className="mt-2 text-2xl font-bold text-ink-800">{texto ? valor : formatMoney(valor)}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
      {extra && <div className="mt-1">{extra}</div>}
    </div>
  )
}

function MiniBox({ label, valor, cor = 'ink' }: any) {
  const cores: any = { ink: 'text-ink-800', emerald: 'text-emerald-600', amber: 'text-amber-600', rose: 'text-rose-600' }
  return (
    <div className="bg-ink-50 rounded-xl p-3">
      <div className="text-[11px] text-ink-500 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${cores[cor]}`}>{valor}</div>
    </div>
  )
}

function RankingBarras({ itens, cor, mostrarQtd }: any) {
  if (!itens || itens.length === 0) return <Vazio />
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, itens.length * 28)}>
      <BarChart data={itens} layout="vertical" margin={{ left: 0, right: 16 }}>
        <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
        <YAxis type="category" dataKey="nome" width={140} fontSize={10}
          tickFormatter={(v) => v.length > 22 ? v.slice(0, 22) + '…' : v} />
        <Tooltip formatter={(v: any, _n: any, p: any) => [formatMoney(v), mostrarQtd ? `Qtd: ${p.payload.qtd}` : 'Total']} />
        <Bar dataKey="total" radius={[0, 4, 4, 0]} fill={cor} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function CurvaABC({ resumo, unidade }: any) {
  const totalValor = (resumo.A.valor + resumo.B.valor + resumo.C.valor) || 1
  return (
    <div>
      <div className="flex h-6 rounded-lg overflow-hidden mb-3">
        {['A', 'B', 'C'].map(k => {
          const w = resumo[k].valor / totalValor * 100
          return w > 0 ? <div key={k} style={{ width: `${w}%`, background: ABC_CORES[k] }}
            className="grid place-items-center text-white text-[10px] font-bold" title={`Classe ${k}`}>
            {w > 8 ? `${k} ${w.toFixed(0)}%` : ''}</div> : null
        })}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        {['A', 'B', 'C'].map(k => (
          <div key={k} className="rounded-xl p-2" style={{ background: `${ABC_CORES[k]}15` }}>
            <div className="font-bold" style={{ color: ABC_CORES[k] }}>Classe {k}</div>
            <div className="text-ink-700">{resumo[k].qtd} {unidade}</div>
            <div className="text-xs text-ink-500">{resumo[k].pct}% da venda</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function CarregandoBox() {
  return <div className="grid place-items-center py-20 text-ink-400"><Loader2 className="animate-spin" size={28} /></div>
}
function Vazio({ txt = 'Sem dados no período' }: { txt?: string }) {
  return <div className="text-center text-ink-400 text-sm py-8">{txt}</div>
}
